import { useState } from 'react';
import { Button } from '@lms/ui';
import { AlertCircle, Upload, X } from 'lucide-react';
import type { User, UserGender } from '@lms/shared/types';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import { copy } from '../copy/copy';
import { useAuthStore } from '../store/auth';
import { Bilingual } from './Bilingual';
import { FacelessAvatar } from './FacelessAvatar';
import { useLanguage } from '../lib/LanguageContext';

interface EditProfileModalProps {
  onClose: () => void;
}

interface ProfileFormData {
  name: string;
  phone: string;
  gender: UserGender;
  dateOfBirth: string;
}

function initFormData(user: ReturnType<typeof useAuthStore.getState>['user']): ProfileFormData {
  return {
    name: user?.name || '',
    phone: user?.phone || '',
    gender: user?.gender === 'FEMALE' ? 'FEMALE' : 'MALE',
    dateOfBirth: user?.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '',
  };
}

export function EditProfileModal({ onClose }: EditProfileModalProps) {
  const { user, setAuth, token } = useAuthStore();
  const { t } = useLanguage();
  const [formData, setFormData] = useState<ProfileFormData>(() => initFormData(user));
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const updateMutation = useMutation({
    mutationFn: async (data: ProfileFormData) => {
      let avatarUrl = user?.avatar;
      if (avatarFile) {
        const uploadResponse = await apiClient.storage.upload(avatarFile);
        avatarUrl = uploadResponse.url;
      }

      const updateData: Partial<User> = {
        name: data.name,
        gender: data.gender,
      };

      if (data.phone) updateData.phone = data.phone;
      if (data.dateOfBirth) updateData.dateOfBirth = new Date(`${data.dateOfBirth}T00:00:00`);
      if (avatarUrl) updateData.avatar = avatarUrl;

      return apiClient.auth.updateProfile(updateData);
    },
    onSuccess: updatedUser => {
      if (token) setAuth(token, updatedUser);
      onClose();
    },
    onError: error => {
      setErrorMessage(error instanceof Error ? error.message : t(copy.profile.errorMsg));
    },
  });

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/45 p-4 backdrop-blur-sm"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-profile-title"
        className="w-full max-w-md overflow-hidden rounded-[1.5rem] bg-surface-container-lowest shadow-level3"
      >
        <header className="flex items-center justify-between gap-4 bg-surface-container-low p-5">
          <div>
            <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em] text-primary">
              <Bilingual {...copy.profile.dataProfile} />
            </p>
            <h3
              id="edit-profile-title"
              className="m-0 mt-1 text-lg font-headline font-bold text-on-surface"
            >
              <Bilingual {...copy.profile.editProfile} />
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t(copy.common.close)}
            className="flex size-9 items-center justify-center rounded-xl text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </header>

        <form
          onSubmit={event => {
            event.preventDefault();
            setErrorMessage('');
            updateMutation.mutate(formData);
          }}
          className="max-h-[78vh] space-y-5 overflow-y-auto p-5"
        >
          {errorMessage && (
            <div
              className="flex items-start gap-3 rounded-xl bg-error-container p-3 text-sm text-on-error-container"
              role="alert"
            >
              <AlertCircle size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div>
            <label className="mb-2 block text-sm font-label font-bold text-on-surface">
              <Bilingual {...copy.profile.avatar} />
            </label>
            <div className="flex items-center gap-4 rounded-2xl bg-surface-container-low p-4">
              <div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-primary text-on-primary">
                {avatarFile ? (
                  <img
                    src={URL.createObjectURL(avatarFile)}
                    alt=""
                    className="size-full object-cover"
                  />
                ) : user?.avatar ? (
                  <img src={user.avatar} alt="" className="size-full object-cover" />
                ) : (
                  <FacelessAvatar className="size-9" />
                )}
              </div>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-surface-container-lowest px-4 py-2.5 text-xs font-bold text-primary shadow-level1 transition-colors hover:bg-surface-container-high focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-primary">
                <Upload size={16} aria-hidden="true" />
                {t(copy.profile.uploadImage)}
                <input
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={event => {
                    const file = event.target.files?.[0];
                    if (file) setAvatarFile(file);
                  }}
                />
              </label>
            </div>
          </div>

          <div>
            <label
              htmlFor="profile-name"
              className="mb-2 block text-sm font-label font-bold text-on-surface"
            >
              <Bilingual {...copy.profile.nameShort} />
            </label>
            <input
              id="profile-name"
              type="text"
              required
              value={formData.name}
              onChange={event => setFormData({ ...formData, name: event.target.value })}
              className="w-full rounded-xl bg-surface-container-high px-4 py-3 text-on-surface outline-2 outline-transparent transition-colors focus:outline-primary"
            />
          </div>

          <div>
            <label
              htmlFor="profile-phone"
              className="mb-2 block text-sm font-label font-bold text-on-surface"
            >
              <Bilingual {...copy.profile.phone} />
            </label>
            <input
              id="profile-phone"
              type="tel"
              value={formData.phone}
              onChange={event => setFormData({ ...formData, phone: event.target.value })}
              className="w-full rounded-xl bg-surface-container-high px-4 py-3 text-on-surface outline-2 outline-transparent transition-colors focus:outline-primary"
            />
          </div>

          <div>
            <label
              htmlFor="profile-gender"
              className="mb-2 block text-sm font-label font-bold text-on-surface"
            >
              <Bilingual {...copy.profile.gender} />
            </label>
            <select
              id="profile-gender"
              value={formData.gender}
              onChange={event =>
                setFormData({ ...formData, gender: event.target.value as UserGender })
              }
              className="w-full rounded-xl bg-surface-container-high px-4 py-3 text-on-surface outline-2 outline-transparent transition-colors focus:outline-primary"
            >
              <option value="MALE">{t(copy.profile.genderMale)}</option>
              <option value="FEMALE">{t(copy.profile.genderFemale)}</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="profile-date-of-birth"
              className="mb-2 block text-sm font-label font-bold text-on-surface"
            >
              <Bilingual {...copy.profile.dateOfBirth} />
            </label>
            <input
              id="profile-date-of-birth"
              type="date"
              value={formData.dateOfBirth}
              onChange={event => setFormData({ ...formData, dateOfBirth: event.target.value })}
              className="w-full rounded-xl bg-surface-container-high px-4 py-3 text-on-surface outline-2 outline-transparent transition-colors focus:outline-primary"
            />
          </div>

          <div className="flex flex-wrap justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={updateMutation.isPending}
            >
              <Bilingual {...copy.common.cancel} />
            </Button>
            <Button type="submit" variant="primary" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? (
                t(copy.profile.saving)
              ) : (
                <Bilingual {...copy.common.save} />
              )}
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}
